import { useObjectUrl, watchDebounced } from '@vueuse/core';
import { computed, ComputedRef, ref, Ref, watch } from 'vue';
import { IMenuQrCopy } from '@/domain/menu-qr/menu-qr-copy';
import { TMenuQrPosterSize } from '@/domain/menu-qr/menu-qr-poster';
import { IMenuQrTarget } from '@/domain/menu-qr/menu-qr-targets';
import { loadOptionalImage } from '@/lib/print/menu-qr-canvas';
import {
  canvasToPngBlob,
  renderMenuQrPoster,
} from '@/lib/print/menu-qr-poster-renderer';

const RENDER_DEBOUNCE_MS = 150;

export interface IMenuQrPreviewInput {
  targets: Ref<IMenuQrTarget[]>;
  size: Ref<TMenuQrPosterSize>;
  copy: Ref<IMenuQrCopy | null>;
  logoUrl: Ref<string | null>;
}

export interface IUseMenuQrPreview {
  index: Ref<number>;
  total: ComputedRef<number>;
  current: ComputedRef<IMenuQrTarget | null>;
  /** Object URL of the exact PNG that the download would contain. */
  imageUrl: Readonly<Ref<string | undefined>>;
  rendering: Ref<boolean>;
  failed: Ref<boolean>;
  next(): void;
  prev(): void;
}

/**
 * Renders the currently paged target at print resolution (debounced, stale
 * renders dropped) and exposes it as an object URL for an <img>.
 */
export function useMenuQrPreview(
  input: IMenuQrPreviewInput
): IUseMenuQrPreview {
  const index = ref(0);
  const total = computed(() => input.targets.value.length);
  const current = computed(() => input.targets.value[index.value] ?? null);
  const blob = ref<Blob | undefined>(undefined);
  const imageUrl = useObjectUrl(blob);
  const rendering = ref(false);
  const failed = ref(false);
  let generation = 0;
  // A logo that failed to load is not requested again on every re-render (the
  // download resolves it once more per batch, so a transient failure only affects
  // the preview until the page is reopened).
  let unavailableLogoUrl: string | null = null;

  // Keep showing the same target when the list changes; otherwise clamp.
  watch(input.targets, (targets, previous) => {
    const shownId = previous?.[index.value]?.id;
    const shownIndex = shownId
      ? targets.findIndex((target) => target.id === shownId)
      : -1;

    index.value =
      shownIndex >= 0
        ? shownIndex
        : Math.min(index.value, Math.max(0, targets.length - 1));
  });

  async function render() {
    const target = current.value;
    const copy = input.copy.value;
    const currentGeneration = ++generation;

    if (!target || !copy) {
      blob.value = undefined;
      rendering.value = false;
      failed.value = false;
      return;
    }

    rendering.value = true;
    failed.value = false;

    try {
      const logoUrl = await resolveLogoUrl(input.logoUrl.value);

      if (currentGeneration !== generation) return;

      // A fresh canvas per render: renders await between drawing steps, so a shared
      // canvas could be painted by two overlapping renders.
      const canvas = await renderMenuQrPoster({
        size: input.size.value,
        design: target.design,
        qrUrl: target.url,
        title: target.title,
        logoUrl,
        steps: copy.steps,
      });
      const png = await canvasToPngBlob(canvas);

      if (currentGeneration !== generation) return;

      blob.value = png;
    } catch (error) {
      if (currentGeneration !== generation) return;

      console.error(error);
      failed.value = true;
      blob.value = undefined;
    } finally {
      if (currentGeneration === generation) rendering.value = false;
    }
  }

  async function resolveLogoUrl(
    logoUrl: string | null
  ): Promise<string | null> {
    if (!logoUrl || logoUrl === unavailableLogoUrl) return null;
    if (await loadOptionalImage(logoUrl)) return logoUrl;

    unavailableLogoUrl = logoUrl;
    return null;
  }

  // Targets are rebuilt on every option change; only re-render when what is drawn changes.
  const renderKey = computed(() => {
    const target = current.value;

    return target
      ? [target.id, target.url, target.design, target.title ?? ''].join('|')
      : '';
  });

  watchDebounced(
    [renderKey, input.size, input.copy, input.logoUrl],
    () => {
      render();
    },
    { debounce: RENDER_DEBOUNCE_MS }
  );
  // First render right away: vueuse routes an `immediate` call through the debounce
  // filter, which would delay the first preview by the debounce time.
  render();

  function next() {
    if (index.value < total.value - 1) index.value++;
  }

  function prev() {
    if (index.value > 0) index.value--;
  }

  return { index, total, current, imageUrl, rendering, failed, next, prev };
}
